import type { ReactNode } from "react";

import { useInfiniteScroll } from "../../hooks";

import styles from "./autocomplete.module.css";

type AutocompleteProps<T extends { id: string }> = {
	/** Rendered when there are no items and the component is not fetching. */
	emptyState?: ReactNode;
	/** Callback to load the next page of results, triggered by the scroll sentinel. */
	fetchNextPage: () => void;
	/** Whether another page of results is available to fetch. */
	hasNextPage: boolean;
	/** Whether any fetch (initial or paginated) is in progress. */
	isFetching: boolean;
	/** Whether a paginated fetch is currently in progress. */
	isFetchingNextPage: boolean;
	/** The current list of items to display in the dropdown. */
	items: T[];
	/** Placeholder rendered at the bottom of the list while the next page is loading. */
	loadingNewPageItem: ReactNode;
	/** Renders a single dropdown option given an item. */
	menuItem: (item: T) => ReactNode;
	/** Called with the selected item's id, or `null` to clear the selection. */
	onChange: (value: null | string) => void;
	/** The search input element rendered above the dropdown list. */
	searchbar: ReactNode;
	/** Placeholder rendered in place of the list while an initial search is in progress. */
	searchItem?: ReactNode;
};
/**
 * Autocomplete based on react-query infinite scroll
 * @see This autocomplete is not fully accessible (i.e. options are not keyboard navigable), but it should be good enough for most use cases.
 * react-aria-component's Autocomplete component is not stable yet.
 *
 * TODO: replace this with `react-aria-component`'s one as soon as it is stable
 */
export function Autocomplete<T extends { id: string }>({
	emptyState,
	fetchNextPage,
	hasNextPage,
	isFetching,
	isFetchingNextPage,
	items,
	loadingNewPageItem,
	menuItem,
	onChange,
	searchbar,
	searchItem,
}: AutocompleteProps<T>): ReactNode {
	const sentinelRef = useInfiniteScroll<HTMLLIElement>({
		fetchNextPage,
		hasNextPage,
		isFetching,
	});

	if (!isFetching && items.length === 0) {
		return (
			<>
				{searchbar}
				{emptyState}
			</>
		);
	}

	return (
		<>
			{searchbar}
			{isFetching && !isFetchingNextPage ? (
				searchItem
			) : (
				<>
					<ul className={styles.list} role="listbox">
						{items.map((item) => {
							return (
								<li
									aria-selected={false}
									key={item.id}
									onClick={() => {
										return onChange(item.id);
									}}
									onPointerDown={(e) => {
										return e.preventDefault();
									}}
									role="option"
								>
									{menuItem(item)}
								</li>
							);
						})}
						{Boolean(hasNextPage && !isFetching) && (
							// The height is required for the intersection observer to work,
							// but it doesn't need to be visible
							<li key="sentinel-ref" ref={sentinelRef} style={{ height: 1 }} />
						)}
					</ul>
					{isFetchingNextPage && loadingNewPageItem}
				</>
			)}
		</>
	);
}
